import LegalLayout, {
  LegalSection,
  Columns,
  Subhead,
  Bullets,
  Note,
} from "@/components/ttt/LegalLayout"
import type { TttChrome } from "@/components/ttt/types"

interface Props {
  chrome: TttChrome
  lastUpdated: string
}

const mail = "turbotimetravel@gmail.com"

export default function Cookies({ chrome, lastUpdated }: Props) {
  return (
    <LegalLayout
      chrome={chrome}
      title="Politique des cookies"
      subtitle="Information transparente sur l'utilisation des cookies sur le site ttt-editions.fr"
      lastUpdated={lastUpdated}
      cta={{
        title: "D'autres questions sur les cookies ?",
        subtitle: "Notre association privilégie la transparence et répond à toutes vos interrogations",
        secondaryHref: chrome.paths.confidentiality,
        secondaryLabel: "Politique de confidentialité",
      }}
    >
      <LegalSection title="1. Qu'est-ce qu'un cookie ?">
        <Subhead>Définition simple</Subhead>
        <p>
          Un cookie est un petit fichier texte stocké sur votre appareil (ordinateur, smartphone,
          tablette) lors de la visite d'un site web. Il permet au site de se "souvenir" de certaines
          informations sur votre visite et vos préférences.
        </p>
      </LegalSection>

      <LegalSection title="2. Notre approche associative et respectueuse">
        <Subhead>Respect de votre vie privée</Subhead>
        <p>
          Chez TTT Corp, association à but non lucratif, nous privilégions une approche minimaliste
          et respectueuse de vos données. Nous utilisons uniquement les cookies essentiels au
          fonctionnement de notre site de vente de livres.
        </p>
        <Columns>
          <div>
            <Subhead>Ce que nous utilisons</Subhead>
            <p className="text-content/80 text-sm">
              Uniquement des cookies techniques essentiels à la commande
            </p>
          </div>
          <div>
            <Subhead>Ce que nous n'utilisons PAS</Subhead>
            <p className="text-content/80 text-sm">
              Aucun cookie de tracking, publicitaire ou de profilage
            </p>
          </div>
          <div>
            <Subhead>Votre confidentialité</Subhead>
            <p className="text-content/80 text-sm">
              Aucune donnée partagée avec des tiers à des fins commerciales
            </p>
          </div>
        </Columns>
      </LegalSection>

      <LegalSection title="3. Les cookies que nous utilisons">
        <Subhead>Cookies techniques essentiels uniquement</Subhead>
        <p>
          Ces cookies sont indispensables au fonctionnement de notre boutique en ligne. Ils
          permettent la gestion de votre panier, votre session de commande et la sécurité du site.
          Ils ne collectent aucune information personnelle identifiable et ne peuvent pas être
          désactivés.
        </p>
        <Columns>
          <div>
            <Subhead>Panier d'achat</Subhead>
            <Bullets
              items={[
                "Maintient les livres sélectionnés",
                "Conserve les quantités choisies",
                "Expire à la fermeture du navigateur",
                "Essentiel pour la commande",
              ]}
            />
          </div>
          <div>
            <Subhead>Protection CSRF</Subhead>
            <Bullets
              items={[
                "Sécurise les formulaires de commande",
                "Prévient les attaques malveillantes",
                "Généré automatiquement par Rails",
                "Essentiel pour votre sécurité",
              ]}
            />
          </div>
        </Columns>
      </LegalSection>

      <LegalSection title="4. Cookies que nous n'utilisons PAS">
        <Subhead>Engagement de transparence</Subhead>
        <p>
          Contrairement à de nombreux sites commerciaux, TTT Corp s'engage à ne pas utiliser de
          cookies intrusifs ou commerciaux. Voici ce que nous ne faisons pas :
        </p>
        <Columns>
          <div>
            <Subhead>Cookies publicitaires</Subhead>
            <Bullets
              items={[
                "Aucun tracking publicitaire",
                "Pas de profilage comportemental",
                "Aucune revente de données",
                "Pas de remarketing",
              ]}
            />
          </div>
          <div>
            <Subhead>Cookies analytiques tiers</Subhead>
            <Bullets
              items={[
                "Pas de Google Analytics",
                "Aucun pixel de tracking",
                "Pas de cookies Facebook",
                "Aucune collecte comportementale",
              ]}
            />
          </div>
        </Columns>
        <Note title="Philosophie associative">
          En tant qu'association à but non lucratif, notre seul objectif est de vous proposer nos
          livres dans le respect total de votre vie privée.
        </Note>
      </LegalSection>

      <LegalSection title="5. Cookies de paiement sécurisé">
        <p>
          Pour le traitement sécurisé des paiements, nous utilisons Stripe, leader mondial des
          paiements en ligne.
        </p>
        <Subhead>Paiements Stripe</Subhead>
        <p>
          Stripe utilise ses propres cookies pour sécuriser les transactions et prévenir la fraude,
          conformément à sa propre politique de confidentialité.
        </p>
        <Columns>
          <div>
            <Subhead>Sécurité maximale</Subhead>
            <Bullets
              items={[
                "Chiffrement SSL/TLS",
                "Protection anti-fraude",
                "Conformité PCI DSS",
                "Données bancaires non stockées",
              ]}
            />
          </div>
          <div>
            <Subhead>Votre protection</Subhead>
            <Bullets
              items={[
                "Cartes bancaires acceptées",
                "PayPal et wallets",
                "Transactions 3D Secure",
                "Remboursements sécurisés",
              ]}
            />
          </div>
        </Columns>
        <Note title="Informations importantes">
          <p className="mb-2">
            TTT Corp ne stocke jamais vos informations de paiement. Toutes les données bancaires
            sont traitées directement par Stripe.
          </p>
          <p>
            Consultez la{" "}
            <a
              href="https://stripe.com/fr/privacy"
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              politique de confidentialité de Stripe
            </a>{" "}
            pour plus d'informations.
          </p>
        </Note>
      </LegalSection>

      <LegalSection title="6. Comment gérer les cookies ?">
        <p>
          Vous avez le contrôle total sur les cookies, bien que ceux que nous utilisons soient
          essentiels au bon fonctionnement de la boutique.
        </p>
        <Columns>
          <div>
            <Subhead>Paramètres du navigateur</Subhead>
            <Bullets
              items={[
                "Accédez aux paramètres de votre navigateur",
                'Section "Confidentialité et sécurité"',
                "Gérez les cookies par site",
                "Supprimez les cookies existants",
              ]}
            />
          </div>
          <div>
            <Subhead>Impact sur la boutique</Subhead>
            <Bullets
              items={[
                "Sans cookies : impossibilité de commander",
                "Panier non fonctionnel",
                "Session de connexion impossible",
                "Sécurité compromise",
              ]}
            />
          </div>
        </Columns>
        <Note title="Recommandation">
          Nous recommandons de conserver nos cookies essentiels activés pour profiter pleinement de
          votre expérience d'achat sur ttt-editions.fr.
        </Note>
      </LegalSection>

      <LegalSection title="7. Questions sur les cookies ?">
        <Subhead>Nous sommes transparents</Subhead>
        <p>
          Si vous avez des questions sur notre utilisation des cookies ou notre politique de
          confidentialité, n'hésitez pas à nous contacter.
        </p>
        <p>
          Email :{" "}
          <a href={`mailto:${mail}`} className="text-primary hover:underline">
            {mail}
          </a>
        </p>
        <Note title="Engagement associatif">
          TTT Corp, en tant qu'association à but non lucratif, s'engage à maintenir une approche
          éthique et respectueuse de vos données personnelles.
        </Note>
      </LegalSection>
    </LegalLayout>
  )
}
